import {
  Badge,
  Body1,
  Button,
  Spinner,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Database24Filled,
  Database24Regular,
} from '@fluentui/react-icons';
import { useId, useMemo, useState } from 'react';

import {
  DATASET_CATALOG_GENERIC_TYPE,
  DATASET_CATALOG_NEW_WINDOW_DAYS,
  DATASET_CATALOG_TYPE_ORDER,
} from '../../../constants/index.js';
import type { Dataset } from '../../../services/dataHubService.interface.js';

import type { DatasetTileGalleryProps } from './DatasetTileGallery.interface.js';
import { useStyles } from './DatasetTileGallery.style.js';

const DatabaseIcon = bundleIcon(
  Database24Filled,
  Database24Regular,
) as React.FC;

interface DatasetGroup {
  dataType: string;
  datasets: Dataset[];
}

const TYPE_PRIORITY = new Map<string, number>(
  DATASET_CATALOG_TYPE_ORDER.map((dataType, index) => [dataType, index]),
);
const GENERIC_PRIORITY =
  TYPE_PRIORITY.get(DATASET_CATALOG_GENERIC_TYPE) ?? Number.MAX_SAFE_INTEGER;
const NEW_WINDOW_MS = DATASET_CATALOG_NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;

function compareDataTypes(left: string, right: string): number {
  if (left === right) return 0;
  if (left === DATASET_CATALOG_GENERIC_TYPE) return 1;
  if (right === DATASET_CATALOG_GENERIC_TYPE) return -1;

  const leftPriority = TYPE_PRIORITY.get(left) ?? GENERIC_PRIORITY;
  const rightPriority = TYPE_PRIORITY.get(right) ?? GENERIC_PRIORITY;
  return (
    leftPriority - rightPriority ||
    left.localeCompare(right, 'en-US', { sensitivity: 'base' })
  );
}

function isTestDataset(dataset: Dataset): boolean {
  return dataset.name.toLowerCase().includes('test');
}

function orderTestDatasetsLast(datasets: Dataset[]): Dataset[] {
  const regularDatasets: Dataset[] = [];
  const testDatasets: Dataset[] = [];

  for (const dataset of datasets) {
    const target = isTestDataset(dataset) ? testDatasets : regularDatasets;
    target.push(dataset);
  }

  return [...regularDatasets, ...testDatasets];
}

function groupDatasets(datasets: Dataset[]): DatasetGroup[] {
  const grouped = new Map<string, Dataset[]>();
  for (const dataset of datasets) {
    const dataType = dataset.dataType ?? 'Other';
    grouped.set(dataType, [...(grouped.get(dataType) ?? []), dataset]);
  }

  return Array.from(grouped, ([dataType, groupedDatasets]) => ({
    dataType,
    datasets: orderTestDatasetsLast(groupedDatasets),
  })).sort((left, right) => compareDataTypes(left.dataType, right.dataType));
}

function formatEntryCount(rowCount?: number): string {
  if (rowCount === undefined) return 'Items: —';
  return `Items: ${rowCount.toLocaleString('en-US')}`;
}

function isNewDataset(createdAt?: string): boolean {
  if (createdAt === undefined) return false;
  const createdTime = Date.parse(createdAt);
  if (!Number.isFinite(createdTime)) return false;
  const age = Date.now() - createdTime;
  return age >= 0 && age <= NEW_WINDOW_MS;
}

function DatasetTile({
  dataset,
  onClick,
}: {
  dataset: Dataset;
  onClick: (dataset: Dataset) => void;
}) {
  const styles = useStyles();
  const entryCount = formatEntryCount(dataset.rowCount);
  const description = dataset.description ?? 'No description provided';
  const isDraft = dataset.status === 'Draft';
  const isNew = isNewDataset(dataset.createdAt);
  const isRestricted = dataset.accessLevel === 'Restricted';
  const actionLabelId = useId();
  const nameId = useId();
  const metadataId = useId();
  const accessibleMetadata = [
    dataset.source ?? 'Unknown source',
    isNew ? 'New' : null,
    isRestricted ? 'Restricted' : null,
    `Subtype ${dataset.subType ?? 'not specified'}`,
    entryCount,
    dataset.status,
    `Version ${dataset.version}`,
  ]
    .filter((value): value is string => value !== null)
    .join('. ');

  return (
    <button
      type="button"
      className={styles.tile}
      aria-labelledby={`${actionLabelId} ${nameId}`}
      aria-describedby={metadataId}
      onClick={() => {
        onClick(dataset);
      }}
    >
      <span id={actionLabelId} className={styles.screenReaderOnly}>
        Open dataset
      </span>
      <span id={metadataId} className={styles.screenReaderOnly}>
        {accessibleMetadata}
      </span>
      <span className={styles.tileBadgeRow}>
        <Badge appearance="tint" color="informative" size="small">
          {dataset.source ?? 'Unknown source'}
        </Badge>
        <span className={styles.classificationBadges}>
          {isNew && (
            <Badge appearance="filled" color="brand" size="small">
              New
            </Badge>
          )}
          {isRestricted && (
            <Badge appearance="tint" color="warning" size="small">
              Restricted
            </Badge>
          )}
        </span>
      </span>
      <span id={nameId} className={styles.tileName}>
        {dataset.name}
      </span>
      <Tooltip
        content={description}
        relationship="description"
        positioning="after"
        showDelay={500}
      >
        <span className={styles.tileDescription}>{description}</span>
      </Tooltip>
      <span className={styles.secondaryMetadata}>
        <span>Subtype: {dataset.subType ?? '—'}</span>
        <span>{entryCount}</span>
      </span>
      <span className={styles.tileFooter}>
        <span className={styles.status}>
          <span
            className={mergeClasses(
              styles.statusDot,
              isDraft && styles.draftDot,
            )}
          />
          {dataset.status}
        </span>
        <span>Version {dataset.version}</span>
      </span>
    </button>
  );
}

export function DatasetTileGallery({
  datasets,
  isLoading,
  error,
  onDatasetClick,
}: DatasetTileGalleryProps) {
  const styles = useStyles();
  const groups = useMemo(() => groupDatasets(datasets), [datasets]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  );

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading datasets..." />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className={mergeClasses(styles.messageState, styles.errorState)}>
        <Body1>Failed to load datasets: {error}</Body1>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className={styles.messageState}>
        <DatabaseIcon />
        <Body1>No datasets match your filters.</Body1>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {groups.map((group) => {
        const headingId = `dataset-group-${group.dataType
          .toLowerCase()
          .replace(/ /g, '-')}`;
        const tileGridId = `${headingId}-tiles`;
        const isExpanded = expandedGroups.has(group.dataType);
        const hasHiddenDatasets = group.datasets.some(isTestDataset);
        const visibleDatasets = isExpanded
          ? group.datasets
          : group.datasets.filter((dataset) => !isTestDataset(dataset));

        return (
          <section
            key={group.dataType}
            className={styles.group}
            aria-labelledby={headingId}
          >
            <div className={styles.groupHeader}>
              <span className={styles.groupIcon}>
                <DatabaseIcon />
              </span>
              <span id={headingId} className={styles.groupTitle}>
                {group.dataType}
              </span>
              <span className={styles.groupCount}>
                {group.datasets.length}{' '}
                {group.datasets.length === 1 ? 'dataset' : 'datasets'}
              </span>
              {hasHiddenDatasets && (
                <Button
                  appearance="transparent"
                  className={styles.viewAllButton}
                  aria-controls={tileGridId}
                  aria-expanded={isExpanded}
                  onClick={() => {
                    setExpandedGroups((current) => {
                      const next = new Set(current);
                      if (isExpanded) {
                        next.delete(group.dataType);
                      } else {
                        next.add(group.dataType);
                      }
                      return next;
                    });
                  }}
                >
                  {isExpanded ? 'Show less' : 'View all'}
                </Button>
              )}
            </div>
            <div id={tileGridId} className={styles.tileGrid}>
              {visibleDatasets.map((dataset) => (
                <DatasetTile
                  key={dataset.id}
                  dataset={dataset}
                  onClick={onDatasetClick}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
